import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Search, Check } from 'lucide-react';
import { templateMeta } from './templateTypeMeta';

// Port of platform-app components/team/AddStepDropdown.tsx (RUBY light chrome).

interface TemplateOption {
  id: string;
  name: string;
  templateType?: string;
}

interface AddStepDropdownProps {
  templates: TemplateOption[];   // ALL templates
  addedIds: string[];            // template ids already added as steps
  onSelect: (templateId: string) => void;
  placeholder?: string;          // default "+ Add Step"
}

// Short type badge label shown on each row (Ack / Form / Fillable / Upload / Sheet).
const SHORT_TYPE_LABEL: Record<string, string> = {
  acknowledgment: 'Ack',
  form: 'Form',
  fillable_doc: 'Fillable',
  sheet: 'Sheet',
  upload: 'Upload',
};
const shortTypeLabel = (t?: string) => SHORT_TYPE_LABEL[t || 'upload'] || 'Upload';

const AddStepDropdown: React.FC<AddStepDropdownProps> = ({
  templates, addedIds, onSelect, placeholder = '+ Add Step',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  const addedSet = useMemo(() => new Set(addedIds), [addedIds]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(t => t.name.toLowerCase().includes(q));
  }, [templates, search]);

  // Auto-focus the search box on mouse (fine-pointer) devices only, to avoid
  // popping the on-screen keyboard on touch devices.
  useEffect(() => {
    if (!isOpen) return;
    const fine = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(pointer: fine)').matches;
    if (fine) searchRef.current?.focus();
  }, [isOpen]);

  const close = () => { setIsOpen(false); setSearch(''); };

  const handlePick = (id: string) => {
    if (addedSet.has(id)) return;
    onSelect(id);
    close();
  };

  return (
    <div className="relative inline-block">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(o => !o)}
        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-stone-300 rounded-lg bg-white text-stone-700 hover:bg-stone-50 active:bg-stone-100 transition-colors whitespace-nowrap"
      >
        {placeholder}
        <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Click-outside overlay */}
          <div className="fixed inset-0 z-40" onClick={close} />

          {/* Panel */}
          <div className="absolute right-0 mt-1 w-72 sm:w-80 z-50 border border-stone-200 rounded-lg bg-white shadow-lg">
            {/* Search */}
            <div className="px-3 py-2 border-b border-stone-200">
              <div className="relative">
                <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search templates…"
                  className="w-full pl-6 text-sm text-stone-900 bg-transparent outline-none placeholder-stone-400"
                />
              </div>
            </div>

            {/* List */}
            <div className="max-h-72 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <div className="px-3 py-6 text-center text-xs text-stone-400">No templates found</div>
              ) : (
                filtered.map(t => {
                  const meta = templateMeta(t.templateType);
                  const added = addedSet.has(t.id);
                  return (
                    <div
                      key={t.id}
                      onClick={() => handlePick(t.id)}
                      className={`px-3 py-2 flex items-center gap-2.5 ${
                        added
                          ? 'bg-emerald-50 cursor-default opacity-70'
                          : 'hover:bg-stone-50 cursor-pointer'
                      }`}
                    >
                      <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-sm shrink-0 ${meta.tile}`}>
                        <span aria-hidden>{meta.icon}</span>
                      </span>
                      <span className="flex-1 text-sm text-stone-800 truncate">{t.name}</span>
                      {added ? (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-600 text-white shrink-0">
                          <Check className="w-3 h-3" />
                          Added
                        </span>
                      ) : (
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold shrink-0 ${meta.badge}`}>
                          {shortTypeLabel(t.templateType)}
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AddStepDropdown;
